import type { Job } from "@domain/job";
import type { JobReady } from "@domain/job";
import type { User } from "@domain/user";
import { applyCollectBoundary } from "@domain/job";
import type { LogData } from "@rogoag/shared-ts/dist/logger";
import type { ILogger } from "@infra/interfaces/logger/ILogger";

export interface MarkJobsReadyUseCaseDeps {
	createJobsReady(form: Partial<JobReady>): Promise<JobReady>;
	updateJobs(jobs: Job[], compressed?: boolean): Promise<void>;
	logger: ILogger;
}

/**
 * Use case for marking a batch of jobs as ready: persists a JobReady record
 * (which captures who/when/which jobs) and applies the `collectBoundary`
 * domain transformation to each selected job before bulk-updating them.
 *
 * The two writes happen in sequence — the JobReady record is created first
 * so the workflow audit log is intact even if the bulk Job update fails.
 * Callers see a single thrown error on either failure; recovery is the
 * UI's responsibility.
 */
export class MarkJobsReadyUseCase {
	constructor(private readonly deps: MarkJobsReadyUseCaseDeps) {}

	/**
	 * Mark the supplied jobs as ready. Throws if `selectedJobs` is empty —
	 * the caller's UI guards that, but we preserve the existing contract.
	 */
	async submitForm(selectedJobs: Job[], user?: User): Promise<void> {
		if (!selectedJobs || selectedJobs.length === 0) {
			throw new Error("selectedJobs.length == 0");
		}
		const jobIds = selectedJobs.filter((j) => !!j).map((j) => j.id);
		const logData: LogData = {
			function: "MarkJobsReadyUseCase.submitForm",
			airtable_record_id: jobIds.join(","),
			airtable_table: "Jobs",
		};
		this.deps.logger.log("Submitting Job Ready form", logData);

		const jobReadyForm: Partial<JobReady> = {
			name: "",
			jobIds,
			submitter: user?.name ?? "",
		};

		// Audit-warn if any selected job is already marked — preserves the
		// pre-existing diagnostic surface.
		for (const job of selectedJobs) {
			if (job?.isMarkedReady) {
				this.deps.logger.warning(
					`Job ${job.id} already marked as ready, but an additional Job Ready form is being attached`,
					{
						...logData,
						airtable_record_id: job.id,
					},
				);
			}
		}

		await this.deps.createJobsReady(jobReadyForm);
		this.deps.logger.log("Job Ready form submitted successfully", logData);

		for (const job of selectedJobs) {
			applyCollectBoundary(job);
		}

		await this.deps.updateJobs(selectedJobs, true);
		this.deps.logger.log("Jobs updated successfully after marking as ready", logData);
	}
}
